// CustomControlBar.tsx
import * as React from "react";
import {
  DisconnectButton,
  LeaveIcon,
  MediaDeviceMenu,
  TrackToggle,
  useLocalParticipant,
  usePersistentUserChoices,
} from "@livekit/components-react";
import { Track } from "livekit-client";
import { useTranslation } from "react-i18next";

type Props = {
  className?: string;
  /** Toggle whether the Screen Share control is shown (default: true) */
  enableScreenShare?: boolean;
  /** Show text labels inside buttons (default: true) */
  showText?: boolean;
  /** Show icons inside buttons (default: true) */
  showIcon?: boolean;
  /** Called when screen share is toggled */
  onScreenShareChange?: (enabled: boolean, userInitiated: boolean) => void;
  /** Bubble up device errors from the Screen Share toggle */
  onDeviceError?: (args: { source: Track.Source; error: unknown }) => void;
};

export function CustomControlBar({
  className = "",
  enableScreenShare = true,
  showText = true,
  showIcon = true,
  onScreenShareChange,
  onDeviceError,
}: Props) {
  const { t } = useTranslation();

  const {
    saveAudioInputEnabled,
    saveVideoInputEnabled,
    saveAudioInputDeviceId,
    saveVideoInputDeviceId,
  } = usePersistentUserChoices({ preventSave: false });

  const { localParticipant } = useLocalParticipant();
  const isScreenShareEnabled = !!localParticipant?.isScreenShareEnabled;

  const microphoneOnChange = React.useCallback(
    (enabled: boolean, user: boolean) =>
      user ? saveAudioInputEnabled(enabled) : null,
    [saveAudioInputEnabled]
  );

  const cameraOnChange = React.useCallback(
    (enabled: boolean, user: boolean) =>
      user ? saveVideoInputEnabled(enabled) : null,
    [saveVideoInputEnabled]
  );

  const browserSupportsScreenSharing =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getDisplayMedia === "function";

  return (
    <div className={`lk-control-bar relative ${className}`}>
      <div className="flex items-center gap-2">
        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Microphone}
            className="lk-button"
            onChange={microphoneOnChange}
            showIcon={showIcon}
            title={t("controls.microphone")}
          >
            {showText && t("controls.microphone")}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="audioinput"
              onActiveDeviceChange={(_k, id) =>
                saveAudioInputDeviceId(id ?? "default")
              }
            />
          </div>
        </div>

        <div className="lk-button-group">
          <TrackToggle
            source={Track.Source.Camera}
            className="lk-button"
            onChange={cameraOnChange}
            showIcon={showIcon}
            title={t("controls.camera")}
          >
            {showText && t("controls.camera")}
          </TrackToggle>
          <div className="lk-button-group-menu">
            <MediaDeviceMenu
              kind="videoinput"
              onActiveDeviceChange={(_k, id) =>
                saveVideoInputDeviceId(id ?? "default")
              }
            />
          </div>
        </div>

        {enableScreenShare && browserSupportsScreenSharing && (
          <TrackToggle
            source={Track.Source.ScreenShare}
            className="lk-button"
            showIcon={showIcon}
            onChange={(enabled, user) => onScreenShareChange?.(enabled, user)}
            onDeviceError={(error) =>
              onDeviceError?.({ source: Track.Source.ScreenShare, error })
            }
            title={
              isScreenShareEnabled
                ? t("controls.screenShareStop")
                : t("controls.screenShareStart")
            }
          >
            {showText &&
              (isScreenShareEnabled
                ? t("controls.screenShareStop")
                : t("controls.screenShareStart"))}
          </TrackToggle>
        )}
        <DisconnectButton
          className="lk-disconnect-button"
          title={t("controls.leave")}
        >
          {showIcon && <LeaveIcon className="mr-1" />}
          {showText && t("controls.leave")}
        </DisconnectButton>
      </div>
    </div>
  );
}
